import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useBranding, resolveLogo } from "@/lib/branding";
import { Upload, Save, MailCheck } from "lucide-react";

export default function AdminBranding() {
    const branding = useBranding();
    const [name, setName] = useState("");
    const [tagline, setTagline] = useState("");
    const [previewLogo, setPreviewLogo] = useState(null);
    const [savingMeta, setSavingMeta] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [sendingTest, setSendingTest] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        // Sync once when branding loads
        api.get("/branding").then((r) => {
            setName(r.data.institution_name || "");
            setTagline(r.data.tagline || "");
            setPreviewLogo(resolveLogo(r.data.logo_url));
        });
    }, []);

    const saveMeta = async () => {
        setSavingMeta(true);
        try {
            await api.put("/branding", { institution_name: name, tagline });
            toast.success("Branding saved");
            branding.refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSavingMeta(false);
        }
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image (PNG, JPG, WebP)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/branding/logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreviewLogo(resolveLogo(data.logo_url));
            toast.success("Logo updated");
            branding.refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const sendTestEmail = async () => {
        if (!testEmail.trim()) return;
        setSendingTest(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/admin/test-email", { to_email: testEmail });
            setTestResult(data);
            if (data.sent) {
                toast.success("Test email dispatched via Resend");
            } else {
                toast.error("Resend did not accept the message — check backend logs");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setSendingTest(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-branding-page">
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary">
                ← Back
            </Link>
            <div className="mt-2 mb-8">
                <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                    Brand
                </div>
                <h1 className="font-display text-3xl text-primary">Institutional Branding</h1>
                <p className="text-muted-foreground mt-2">
                    Update the name, tagline, and logo used across portals, certificates, and email.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-border">
                    <CardContent className="p-6 space-y-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                                Identity
                            </div>
                            <h2 className="font-display text-xl text-primary mt-1">Name & Tagline</h2>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="brand-name">Institution name</Label>
                            <Input
                                id="brand-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid="brand-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="brand-tagline">Tagline</Label>
                            <Input
                                id="brand-tagline"
                                value={tagline}
                                onChange={(e) => setTagline(e.target.value)}
                                placeholder="Learn Anywhere. Grow Everywhere."
                                data-testid="brand-tagline-input"
                            />
                        </div>
                        <Button
                            onClick={saveMeta}
                            disabled={savingMeta}
                            className="bg-primary hover:bg-primary/90 mt-2"
                            data-testid="save-branding-btn"
                        >
                            <Save className="h-4 w-4 mr-2" />
                            {savingMeta ? "Saving…" : "Save changes"}
                        </Button>
                    </CardContent>
                </Card>

                <Card className="border-border">
                    <CardContent className="p-6 space-y-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                                Logo
                            </div>
                            <h2 className="font-display text-xl text-primary mt-1">Crest & Mark</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Square PNG/JPG, transparent background recommended.
                            </p>
                        </div>
                        <div className="flex items-center gap-5">
                            <div className="h-24 w-24 rounded-sm border border-border bg-secondary/30 flex items-center justify-center overflow-hidden">
                                {previewLogo ? (
                                    <img
                                        src={previewLogo}
                                        alt="Logo preview"
                                        className="h-full w-full object-contain"
                                    />
                                ) : (
                                    <span className="text-xs text-muted-foreground">No logo</span>
                                )}
                            </div>
                            <div className="flex-1">
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    className="hidden"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                    data-testid="logo-file-input"
                                />
                                <Button
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    data-testid="upload-logo-btn"
                                >
                                    <Upload className="h-4 w-4 mr-2" />
                                    {uploading ? "Uploading…" : "Choose file"}
                                </Button>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Used in header, footer, certificates and welcome emails.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border md:col-span-2">
                    <CardContent className="p-6 space-y-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                                Deliverability check
                            </div>
                            <h2 className="font-display text-xl text-primary mt-1">Send a test welcome email</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Sends the production welcome template (with your logo and branding) to any
                                address via Resend. Use this to confirm your verified domain is wired up.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Input
                                placeholder="inbox@yourcompany.com"
                                type="email"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                data-testid="test-email-input"
                                className="flex-1"
                            />
                            <Button
                                onClick={sendTestEmail}
                                disabled={sendingTest || !testEmail.trim()}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="send-test-email-btn"
                            >
                                <MailCheck className="h-4 w-4 mr-2" />
                                {sendingTest ? "Sending…" : "Send test email"}
                            </Button>
                        </div>
                        {testResult && (
                            <div
                                className={`p-4 rounded-sm border text-sm ${
                                    testResult.sent
                                        ? "bg-accent/10 border-accent/40 text-foreground"
                                        : "bg-destructive/10 border-destructive/40 text-destructive"
                                }`}
                                data-testid="test-email-result"
                            >
                                <div className="font-medium">
                                    {testResult.sent ? "✓ Email dispatched" : "✕ Resend rejected"}
                                </div>
                                <div className="text-xs mt-1 text-foreground/70">
                                    Sender: <span className="font-mono">{testResult.sender}</span>{" "}
                                    → <span className="font-mono">{testResult.to}</span>
                                </div>
                                {testResult.email_id && (
                                    <div className="text-xs mt-1 text-foreground/70">
                                        Resend ID: <span className="font-mono">{testResult.email_id}</span>
                                    </div>
                                )}
                                <div className="text-xs mt-1">{testResult.message}</div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
